import clsx from 'clsx';
import Image, { type ImageProps } from 'next/image';
import type { CSSProperties } from 'react';

import { asAbsoluteURL } from '@commons/helpers/url';
import type { DrupalImage as DrupalImageType } from '@commons/types/drupal';
import { NEXT_IMAGE_QUALITY } from '@commons/utils/constants';

// Type definition for the DrupalImage component.

type Props = DrupalImageType & Partial<Omit<ImageProps, 'id'>>;

/**
 * DrupalImage component.
 *
 * Given a Drupal image payload, renders an image using the Next.js Image component.
 * If the image is an SVG, it will render a standard img tag.
 */

const DrupalImage = (props: Props) => {
  // Extract Drupal-specific properties to avoid spreading them onto the component.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, type, name, title, bundle, ...imgProps } = props;

  const {
    url,
    alt,
    width,
    height,
    fill,
    priority,
    focalPoint,
    className,
    style,
    optimize = true,
    ...rest
  } = imgProps;

  if (!url) return null;

  const absoluteUrl = asAbsoluteURL(url);
  const extension = absoluteUrl.split('.').pop();

  if (extension === 'svg') {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={absoluteUrl}
        alt={alt}
        title={alt}
        className={clsx('w-100', className)}
        style={style}
      />
    );
  }

  const hasFocalPoint = focalPoint && focalPoint.relative;
  const inlineStyles: CSSProperties = {
    objectPosition: hasFocalPoint && `${focalPoint.relative.x} ${focalPoint.relative.y}`,
    ...style,
  };

  return (
    <Image
      src={absoluteUrl}
      alt={alt}
      fill={fill}
      title={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      quality={optimize ? NEXT_IMAGE_QUALITY : undefined}
      unoptimized={!optimize}
      priority={priority}
      fetchPriority={priority ? 'high' : 'auto'}
      className={className}
      style={inlineStyles}
      {...rest}
    />
  );
};

export default DrupalImage;
